import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarClock, ExternalLink, Check, Loader2, AlertTriangle } from "lucide-react";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function urgencyColor(days) {
  if (days === null) return "#A8A29E";
  if (days < 0) return "#B91C1C"; // overdue
  if (days === 0) return "#B85C38"; // today
  if (days <= 3) return "#EAB308"; // soon
  return "#4A5D23"; // later
}

function urgencyLabel(days) {
  if (days === null) return "";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days}d`;
}

export default function MyFollowups({ onOpenContact }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reminders/mine");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load reminders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/reminders/${id}/dismiss`);
      toast.success("Reminder dismissed");
      setItems((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const openContact = async (contactId) => {
    try {
      const { data } = await api.get(`/contacts/${contactId}`);
      onOpenContact?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to open contact");
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center text-stone-500" data-testid="followups-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading follow-ups…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="border border-dashed border-stone-300 rounded-sm p-10 text-center" data-testid="followups-empty">
        <CalendarClock className="h-8 w-8 text-stone-400 mx-auto mb-3" />
        <div className="font-display font-semibold text-stone-900">No active follow-ups</div>
        <p className="text-sm text-stone-600 mt-1 max-w-md mx-auto">
          Open a contact and use <span className="font-medium">Set a follow-up reminder</span> to schedule
          a nudge. Reminders you create will show up here.
        </p>
      </div>
    );
  }

  const overdue = items.filter((r) => daysUntil(r.remind_date) < 0);
  const today = items.filter((r) => daysUntil(r.remind_date) === 0);
  const upcoming = items.filter((r) => daysUntil(r.remind_date) > 0);

  return (
    <div className="space-y-6" data-testid="followups-list">
      {overdue.length > 0 && (
        <Section
          title="Overdue"
          count={overdue.length}
          testId="followups-overdue"
          badgeIcon={<AlertTriangle className="h-3.5 w-3.5" />}
        >
          {overdue.map((r) => (
            <FollowupRow key={r.id} r={r} busyId={busyId} onOpen={openContact} onDismiss={dismiss} />
          ))}
        </Section>
      )}
      {today.length > 0 && (
        <Section title="Due Today" count={today.length} testId="followups-today">
          {today.map((r) => (
            <FollowupRow key={r.id} r={r} busyId={busyId} onOpen={openContact} onDismiss={dismiss} />
          ))}
        </Section>
      )}
      {upcoming.length > 0 && (
        <Section title="Upcoming" count={upcoming.length} testId="followups-upcoming">
          {upcoming.map((r) => (
            <FollowupRow key={r.id} r={r} busyId={busyId} onOpen={openContact} onDismiss={dismiss} />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, testId, badgeIcon, children }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline gap-2 mb-3">
        <h2 className="font-display font-bold tracking-tight text-lg text-stone-900 flex items-center gap-1.5">
          {badgeIcon}
          {title}
        </h2>
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500">
          {count} {count === 1 ? "reminder" : "reminders"}
        </span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function FollowupRow({ r, busyId, onOpen, onDismiss }) {
  const days = daysUntil(r.remind_date);
  const color = urgencyColor(days);
  const label = urgencyLabel(days);
  return (
    <div
      className="flex items-start gap-3 border border-stone-200 rounded-sm p-3 bg-white hover:border-stone-300 transition-colors"
      data-testid={`followup-${r.id}`}
    >
      <div className="flex flex-col items-center pt-0.5">
        <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-stone-900" data-testid={`followup-name-${r.id}`}>
            {r.contact_name || "Contact"}
          </span>
          <span
            className="text-[10px] font-mono uppercase tracking-wider"
            style={{ color }}
          >
            {label}
          </span>
          <span className="text-[10px] font-mono text-stone-500 ml-auto">
            {r.remind_date}
          </span>
        </div>
        {r.note && (
          <div className="text-sm text-stone-700 mt-1 whitespace-pre-wrap">{r.note}</div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onOpen(r.contact_id)}
          className="h-7 rounded-sm border-stone-300 text-xs"
          data-testid={`followup-open-${r.id}`}
        >
          <ExternalLink className="h-3 w-3 mr-1" /> Open
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busyId === r.id}
          onClick={() => onDismiss(r.id)}
          className="h-7 rounded-sm border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23] hover:text-white text-xs"
          data-testid={`followup-dismiss-${r.id}`}
        >
          <Check className="h-3 w-3 mr-1" /> Done
        </Button>
      </div>
    </div>
  );
}
